import type { TokenStatus, TokenVerifyResult } from '@volley/crucible-ipc-types/ipc-channels';
import { useIpcQuery } from './hooks/useIpc';
import { isCompleteTokenVerifyResult } from './token-verify';

export type LocalSetupDestination = 'connections' | 'diagnostics';

export interface LocalReadinessIssue {
  id: 'github' | 'anthropic' | 'git' | 'node' | 'pnpm' | 'verification';
  label: string;
  reason: string;
  destination: LocalSetupDestination;
}

export interface LocalOperationalReadiness {
  state: 'unavailable' | 'checking' | 'ready' | 'degraded';
  creationReady: boolean;
  issues: LocalReadinessIssue[];
  falConfigured: boolean;
  agentAvailable: boolean;
  gitWorkflowAvailable: boolean;
  toolchainAvailable: boolean;
  previewAvailable: boolean;
  publishAvailable: boolean;
}

function credentialIssue(
  id: 'github' | 'anthropic',
  detail: TokenVerifyResult[typeof id],
): LocalReadinessIssue | null {
  if (detail.status === 'valid') return null;
  const label = id === 'github' ? 'GitHub' : 'Anthropic';
  return {
    id,
    label,
    reason:
      detail.error ??
      (detail.status === 'missing'
        ? `${label} is not connected.`
        : `${label} could not be verified.`),
    destination: 'connections',
  };
}

function toolIssue(
  id: 'git' | 'node' | 'pnpm',
  detail: TokenVerifyResult[typeof id],
): LocalReadinessIssue | null {
  if (detail.available) return null;
  const label = id === 'git' ? 'Git' : id === 'node' ? 'Node.js' : 'pnpm';
  return {
    id,
    label,
    reason:
      detail.reason === 'below-minimum'
        ? `${label} must be updated before Foundry can use it.`
        : `${label} is unavailable to Foundry.`,
    destination: 'diagnostics',
  };
}

/**
 * Derives Local capability state from the readiness query lifecycle.
 *
 * Args:
 *   input: Query enablement, results, and lifecycle flags.
 *
 * Returns:
 *   The operational capabilities and actionable issues supported by current evidence.
 *
 * Raises:
 *   None.
 */
export function deriveLocalOperationalReadiness(input: {
  enabled: boolean;
  verify?: TokenVerifyResult;
  tokens?: TokenStatus;
  loading: boolean;
  error: boolean;
}): LocalOperationalReadiness {
  // Disabled queries provide no evidence of a setup failure.
  if (!input.enabled) {
    return {
      state: 'unavailable',
      creationReady: false,
      issues: [],
      falConfigured: false,
      agentAvailable: false,
      gitWorkflowAvailable: false,
      toolchainAvailable: false,
      previewAvailable: false,
      publishAvailable: false,
    };
  }

  if (input.loading && !input.verify) {
    return {
      state: 'checking',
      creationReady: false,
      issues: [],
      falConfigured: input.tokens?.fal === true,
      agentAvailable: false,
      gitWorkflowAvailable: false,
      toolchainAvailable: false,
      previewAvailable: false,
      publishAvailable: false,
    };
  }

  // The wire type declares every section present, but a runtime payload can
  // arrive partial (#3227: a tokens:verify result without `anthropic` crashed
  // the whole NewGame render inside credentialIssue). A partial result is a
  // verification failure — report it as degraded, never throw mid-render.
  const verify: Partial<TokenVerifyResult> | undefined = input.verify;
  if (input.error || !isCompleteTokenVerifyResult(verify)) {
    return {
      state: 'degraded',
      creationReady: false,
      issues: [
        {
          id: 'verification',
          label: 'Local setup',
          reason: "Foundry could not verify this Mac's credentials and developer tools.",
          destination: 'diagnostics',
        },
      ],
      falConfigured: input.tokens?.fal === true,
      agentAvailable: false,
      gitWorkflowAvailable: false,
      toolchainAvailable: false,
      previewAvailable: false,
      publishAvailable: false,
    };
  }

  const issues = [
    credentialIssue('github', verify.github),
    credentialIssue('anthropic', verify.anthropic),
    toolIssue('git', verify.git),
    toolIssue('node', verify.node),
    toolIssue('pnpm', verify.pnpm),
  ].filter((issue): issue is LocalReadinessIssue => issue !== null);
  const githubReady = verify.github.status === 'valid';
  const anthropicReady = verify.anthropic.status === 'valid';
  const gitReady = verify.git.available;
  const toolchainAvailable = verify.node.available && verify.pnpm.available;

  return {
    state: issues.length === 0 ? 'ready' : 'degraded',
    creationReady: issues.length === 0,
    issues,
    falConfigured: input.tokens?.fal === true,
    agentAvailable: anthropicReady,
    gitWorkflowAvailable: githubReady && gitReady,
    toolchainAvailable,
    previewAvailable: toolchainAvailable,
    publishAvailable: githubReady && gitReady && toolchainAvailable,
  };
}

/**
 * Queries and derives Local operational readiness when the runtime supports it.
 *
 * Args:
 *   enabled: Whether the current runtime can perform Local readiness checks.
 *
 * Returns:
 *   The current Local readiness state and a function that refreshes both queries.
 *
 * Raises:
 *   None.
 */
export function useLocalOperationalReadiness(
  enabled: boolean,
): LocalOperationalReadiness & { refetch: () => Promise<void> } {
  const verify = useIpcQuery('tokens:verify', undefined, { enabled, staleTime: 0 });
  const tokens = useIpcQuery('tokens:get', undefined, { enabled, staleTime: 0 });
  const readiness = deriveLocalOperationalReadiness({
    enabled,
    verify: verify.data,
    tokens: tokens.data,
    loading: enabled && (verify.isLoading || tokens.isLoading),
    // Verification governs readiness; token status only supplies optional metadata such as Fal.ai.
    error: enabled && verify.isError,
  });
  return {
    ...readiness,
    refetch: async () => {
      await Promise.all([verify.refetch(), tokens.refetch()]);
    },
  };
}

/**
 * Summarises Local readiness for user-facing setup guidance.
 *
 * Args:
 *   readiness: The derived Local operational readiness state.
 *
 * Returns:
 *   A concise explanation of availability or required setup.
 *
 * Raises:
 *   None.
 */
export function localReadinessSummary(readiness: LocalOperationalReadiness): string {
  if (readiness.state === 'unavailable') {
    return 'Local setup checks are unavailable in this Foundry runtime.';
  }
  if (readiness.state === 'checking') return 'Checking this Mac…';
  if (readiness.creationReady) {
    return readiness.falConfigured
      ? 'This Mac is ready to build.'
      : 'This Mac is ready. Fal.ai is optional; Foundry will use fallback artwork.';
  }
  const labels = readiness.issues.map((issue) => issue.label).join(', ');
  return `${labels} ${readiness.issues.length === 1 ? 'needs' : 'need'} attention.`;
}

export function localBuildUnavailableReason(
  placement: 'local' | 'cloud',
  readiness: Pick<LocalOperationalReadiness, 'agentAvailable' | 'toolchainAvailable'>,
): string | undefined {
  if (placement === 'cloud' || (readiness.agentAvailable && readiness.toolchainAvailable)) {
    return undefined;
  }
  return 'Local build is unavailable. Connect and verify Anthropic, then make sure Node.js and pnpm are ready in Settings.';
}
